'use client';

import React, { useState } from 'react';
import { Mail, XCircle, CheckCircle2, ArrowLeft } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { AuthSubmitButton } from './AuthSubmitButton';

interface ForgotPasswordFormProps {
    onBack: () => void;
}

export const ForgotPasswordForm: React.FC<ForgotPasswordFormProps> = ({ onBack }) => {
    const t = useTranslations('auth');
    const [email, setEmail] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [sent, setSent] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        try {
            const response = await fetch('/api/auth/reset-password', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email }),
            });

            let data: { error?: string } = {};
            try {
                data = await response.json();
            } catch {
                data = {};
            }

            if (!response.ok) {
                // The route's own copy where it has any ("Invalid email",
                // "Too many requests"); never a browser-internal message.
                setError(data.error || t('errors.network'));
                return;
            }

            setSent(true);
        } catch {
            // A rejected fetch or a dropped connection. Previously this surfaced
            // the raw "Failed to fetch" string with no guidance.
            setError(t('errors.network'));
        } finally {
            setLoading(false);
        }
    };

    if (sent) {
        return (
            <div className="flex flex-col items-center text-center animate-in fade-in slide-in-from-bottom-4 duration-500">
                <div className="h-20 w-20 rounded-full bg-elevated flex items-center justify-center mb-8 ring-1 ring-border">
                    <CheckCircle2 className="w-10 h-10 text-fg" />
                </div>
                <h2 className="text-sm font-semibold text-fg mb-3">Check your email</h2>
                <p className="text-sm text-fg-secondary leading-relaxed max-w-[280px] mb-8">
                    If an account exists for <span className="text-fg">{email}</span>, we&apos;ve sent a password reset link.
                </p>
                <Button
                    variant="ghost"
                    size="sm"
                    onClick={onBack}
                    className="text-sm gap-1.5"
                >
                    <ArrowLeft className="w-4 h-4" />
                    Back to log in
                </Button>
            </div>
        );
    }

    return (
        <>
            <div className="mb-8 text-center">
                <h2 className="text-sm font-semibold text-fg mb-2">Reset password</h2>
                <p className="text-sm text-fg-secondary leading-relaxed max-w-[280px] mx-auto">
                    Enter your email and we&apos;ll send you a reset link.
                </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
                {/* Email field. The visible label is the accessible name; there is no
                    placeholder, so the name can never collapse into one. */}
                <div className="space-y-2">
                    <label htmlFor="forgot-email" className="block text-sm text-fg-secondary">
                        {t('login.email')}
                    </label>
                    <div className="relative group/input">
                        <div className="absolute left-4 top-1/2 -translate-y-1/2 flex items-center justify-center pointer-events-none z-10 h-4 w-4">
                            <Mail className="w-full h-full text-fg-muted group-focus-within/input:text-fg transition-colors" />
                        </div>
                        <Input
                            id="forgot-email"
                            type="email"
                            autoComplete="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                            className="h-11 pl-11"
                        />
                    </div>
                </div>

                {/* A reset failure is announced, not just rendered. */}
                {error && (
                    <div role="alert" className="flex items-start gap-2 animate-in slide-in-from-top-2">
                        <span className="flex h-5 w-4 shrink-0 items-center">
                            <XCircle className="w-4 h-4 text-danger-text" />
                        </span>
                        <p className="text-sm text-danger-text leading-snug">{error}</p>
                    </div>
                )}

                <AuthSubmitButton loading={loading} className="mt-3">
                    Send reset link
                </AuthSubmitButton>
            </form>

            <div className="mt-8 text-center">
                <Button
                    variant="ghost"
                    size="sm"
                    onClick={onBack}
                    className="text-sm gap-1.5 mx-auto"
                >
                    <ArrowLeft className="w-4 h-4" />
                    Back to log in
                </Button>
            </div>
        </>
    );
};
